import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FiCreditCard, FiPlus, FiTrash2 } from "react-icons/fi";
import { useEffect, useMemo, useState } from "react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Card } from "../../ui/Card";
import {
  cardDescriptor,
  cardExpiry,
  cardLabel,
  cardNickname,
} from "../../ui/CardLabel";
import { InlineEdit } from "../../ui/InlineEdit";
import { ApiAuth, ApiError } from "../../../lib/api";
import {
  useCreateSetupIntent,
  useDetachPaymentMethod,
  useInvalidatePaymentMethods,
  usePaymentConfig,
  usePaymentMethods,
  useSetCardNickname,
  useSetDefaultPaymentMethod,
} from "../Billing/hooks";
import { PaymentMethod } from "../Billing/types";

type Props = {
  auth: ApiAuth;
  locationId: string | null;
};

const stripeCache = new Map<string, Promise<Stripe | null>>();
function getStripe(pk: string) {
  if (!stripeCache.has(pk)) stripeCache.set(pk, loadStripe(pk));
  return stripeCache.get(pk)!;
}

// `cardLabel` used to be written out here, and again in three other screens,
// each with its own mask and none of them knowing Stripe spells American
// Express `amex`. It lives in components/ui/CardLabel now.

const AddCardInner = ({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const toast = useToast();

  const onConfirm = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setErrMsg(null);
    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (result.error) {
        setErrMsg(result.error.message || "Could not save card");
        setSubmitting(false);
        return;
      }
      toast({
        title: "Card saved",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      onSuccess();
    } catch (err: any) {
      setErrMsg(err?.message || "Could not save card");
      setSubmitting(false);
    }
  };

  return (
    <>
      <PaymentElement />
      {errMsg && (
        <Text fontSize="13px" color="#b42318" mt={3}>
          {errMsg}
        </Text>
      )}
      <HStack mt={4} justify="flex-end" spacing={2}>
        <Button variant="ghost" onClick={onClose} isDisabled={submitting}>
          Cancel
        </Button>
        <Button
          bg="#1a1f36"
          color="white"
          _hover={{ bg: "#2d3348" }}
          onClick={onConfirm}
          isLoading={submitting}
          loadingText="Saving"
        >
          Save card
        </Button>
      </HStack>
    </>
  );
};

const AddCardDrawer = ({
  isOpen,
  onClose,
  auth,
  stripePublicKey,
}: {
  isOpen: boolean;
  onClose: () => void;
  auth: ApiAuth;
  stripePublicKey: string | null;
}) => {
  const createSetup = useCreateSetupIntent(auth);
  const invalidate = useInvalidatePaymentMethods();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setClientSecret(null);
      setErrMsg(null);
      return;
    }
    if (clientSecret) return;
    createSetup.mutate(undefined, {
      onSuccess: (data) => setClientSecret(data.clientSecret),
      onError: (err) =>
        setErrMsg((err as ApiError)?.message || "Could not start the form"),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const stripePromise =
    stripePublicKey && clientSecret ? getStripe(stripePublicKey) : null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="md" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader fontSize="lg" fontWeight="600" color="#1a1f36">
          Add a payment method
        </DrawerHeader>
        <DrawerBody>
          <Stack spacing={4}>
            {!stripePublicKey && (
              <Text fontSize="13px" color="#b42318">
                Payments are not configured for this clinic. Please contact the
                front desk.
              </Text>
            )}
            {errMsg && (
              <Text fontSize="13px" color="#b42318">
                {errMsg}
              </Text>
            )}
            {!clientSecret && !errMsg && stripePublicKey && (
              <HStack>
                <Spinner size="sm" />
                <Text fontSize="13px" color="#6b7280">
                  Preparing secure form…
                </Text>
              </HStack>
            )}
            {stripePromise && clientSecret && (
              <Elements
                stripe={stripePromise}
                // `locale:"en"` forces the Stripe-hosted UI strings to
                // English regardless of the browser's preferred language —
                // matches the rest of the product copy and avoids leaking
                // Spanish labels into the form.
                options={{
                  clientSecret,
                  appearance: { theme: "stripe" },
                  locale: "en",
                }}
              >
                <AddCardInner
                  onClose={onClose}
                  onSuccess={() => {
                    invalidate();
                    onClose();
                  }}
                />
              </Elements>
            )}
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export const MethodsView = ({ auth, locationId }: Props) => {
  const config = usePaymentConfig(auth, locationId);
  const methods = usePaymentMethods(auth);
  const setDefault = useSetDefaultPaymentMethod(auth);
  const detach = useDetachPaymentMethod(auth);
  const rename = useSetCardNickname(auth);
  const toast = useToast();
  const addDisclosure = useDisclosure();

  const methodsList = methods.data?.methods ?? [];
  const sorted = useMemo(
    () => [...methodsList].sort((a, b) => Number(b.isDefault) - Number(a.isDefault)),
    [methodsList]
  );

  const onDetach = (pm: PaymentMethod) => {
    if (!window.confirm(`Remove ${cardLabel(pm)}?`)) return;
    detach.mutate(pm.id, {
      onError: (err) => {
        toast({
          title: "Could not remove card",
          description: (err as ApiError)?.message || "",
          status: "error",
        });
      },
    });
  };

  // Returns the promise so InlineEdit can close on success and keep the typed
  // text on failure. `mutateAsync` rejects on error, which is what drives that.
  const onRename = (pm: PaymentMethod, nickname: string) =>
    rename
      .mutateAsync({ paymentMethodId: pm.id, nickname })
      .catch((err) => {
        toast({
          title: "Could not rename card",
          description: (err as ApiError)?.message || "",
          status: "error",
        });
        throw err;
      });

  const onSetDefault = (pm: PaymentMethod) => {
    setDefault.mutate(pm.id, {
      onError: (err) => {
        toast({
          title: "Could not update default",
          description: (err as ApiError)?.message || "",
          status: "error",
        });
      },
    });
  };

  return (
    <Box>
      <Card p={{ base: 5, md: 6 }}>
        <Flex justify="space-between" align="center" mb={4}>
          <Box>
            <Text fontSize="16px" fontWeight="600" color="#1a1f36">
              Payment methods
            </Text>
            <Text fontSize="13px" color="#6b7280" mt={1}>
              Manage the cards you use to pay for services.
            </Text>
          </Box>
          <Button
            leftIcon={<FiPlus />}
            bg="#1a1f36"
            color="white"
            _hover={{ bg: "#2d3348" }}
            size="sm"
            borderRadius="8px"
            onClick={addDisclosure.onOpen}
            isDisabled={!config.data?.stripePublicKey}
          >
            Add card
          </Button>
        </Flex>

        {config.error && (
          <Text fontSize="13px" color="#b42318" mb={3}>
            {(config.error as ApiError)?.message ||
              "Could not load payment configuration."}
          </Text>
        )}

        {methods.isLoading && (
          <HStack py={6} justify="center">
            <Spinner size="sm" />
            <Text fontSize="13px" color="#6b7280">
              Loading your cards…
            </Text>
          </HStack>
        )}

        {!methods.isLoading && methods.error && (
          <Text fontSize="13px" color="#b42318">
            {(methods.error as ApiError)?.message ||
              "Could not load your saved cards."}
          </Text>
        )}

        {!methods.isLoading && !methods.error && sorted.length === 0 && (
          <Box
            textAlign="center"
            py={8}
            color="#6b7280"
            fontSize="14px"
            borderWidth="1px"
            borderColor="#f0f0f0"
            borderStyle="dashed"
            borderRadius="8px"
          >
            You don't have any saved cards yet.
          </Box>
        )}

        {!methods.isLoading && sorted.length > 0 && (
          <Stack spacing={2}>
            {sorted.map((pm) => (
              <Flex
                key={pm.id}
                align="center"
                gap={3}
                px={4}
                py={3}
                borderWidth="1px"
                borderColor="#e6e6e6"
                borderRadius="8px"
                bg="white"
              >
                <Box color="#6b7280">
                  <FiCreditCard size={18} />
                </Box>
                <Box flex={1}>
                  <HStack spacing={2}>
                    {/* The card itself is the title, always. A pencil beside a
                        card number reads as "edit the number", which is the one
                        thing a saved card cannot change — so naming lives on its
                        own labelled line below. */}
                    <Text fontSize="14px" fontWeight="500" color="#1a1f36">
                      {cardDescriptor(pm)}
                    </Text>
                    {pm.isDefault && (
                      <Badge
                        bg="#dcfae6"
                        color="#067647"
                        borderRadius="full"
                        px={2}
                        fontSize="11px"
                      >
                        Default
                      </Badge>
                    )}
                  </HStack>
                  <Text fontSize="12px" color="#6b7280" mt={0.5}>
                    {cardExpiry(pm) ? `Expires ${cardExpiry(pm)}` : "-"}
                  </Text>
                  <HStack spacing={1} mt={0.5}>
                    <Text fontSize="12px" color="#6b7280">
                      Nickname:
                    </Text>
                    <InlineEdit
                      value={cardNickname(pm)}
                      onSave={(next) => onRename(pm, next)}
                      editLabel={`Name ${cardDescriptor(pm)}`}
                      placeholder="e.g. My personal Visa"
                      inputWidth="200px"
                    >
                      {/* An unnamed card invites the action instead of showing
                          an empty space next to a bare pencil. */}
                      <Text
                        fontSize="12px"
                        color={cardNickname(pm) ? "#1a1f36" : "#9ca3af"}
                      >
                        {cardNickname(pm) || "Add name"}
                      </Text>
                    </InlineEdit>
                  </HStack>
                </Box>
                {!pm.isDefault && (
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => onSetDefault(pm)}
                    isLoading={setDefault.isPending}
                    borderColor="#e6e6e6"
                  >
                    Make default
                  </Button>
                )}
                <IconButton
                  aria-label="Remove card"
                  icon={<FiTrash2 />}
                  size="xs"
                  variant="ghost"
                  color="#b42318"
                  _hover={{ bg: "#fee4e2" }}
                  onClick={() => onDetach(pm)}
                  isLoading={detach.isPending}
                />
              </Flex>
            ))}
          </Stack>
        )}
      </Card>

      <AddCardDrawer
        isOpen={addDisclosure.isOpen}
        onClose={addDisclosure.onClose}
        auth={auth}
        stripePublicKey={config.data?.stripePublicKey || null}
      />
    </Box>
  );
};
